import { useCallback, useState } from 'react';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Button } from 'foundations-components/transitional/components/Button';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useApprovalContext } from '../../hooks/useApprovalContext';
import { convertToTimestamp, convertTimestampToSimpleDate, convertTimestampToMinutes } from '../../utils/dueDate';
import { equals as simpleDateEquals } from 'foundations-components/transitional/utils/SimpleDate';
import { dueDateUpdateSuccessAlert, genericErrorAlert } from '../../utils/alertUtils';
import { getErrorSubCategoryFromError } from '../../utils/errorUtils';
import { captureException } from '../../utils/sentryUtils';
import { ApprovalDueDateInputGroup } from '../ApprovalDueDateInputGroup';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ApprovalDueDateModal = ({
  currentDueDate,
  onClose
}) => {
  const approval = useApprovalContext();
  const initialDueDate = convertTimestampToSimpleDate(currentDueDate);
  const initialDueTime = convertTimestampToMinutes(currentDueDate);
  const [stagedDueDate, setStagedDueDate] = useState(initialDueDate);
  const [stagedDueTime, setStagedDueTime] = useState(initialDueTime);
  const [isDueDateValid, setIsDueDateValid] = useState(true);
  const onSubmit = useCallback(() => {
    var _approval$data;
    const dueDateTimestamp = stagedDueDate && stagedDueTime !== undefined ? convertToTimestamp(stagedDueDate, stagedDueTime) :
    // If set to <= 0, the BE will remove the due date.
    0;
    const approverOwnerIds = ((_approval$data = approval.data) === null || _approval$data === void 0 ? void 0 : _approval$data.approvalSteps.map(step => step.approver.ownerId)) || [];
    void approval.api.update({
      approverOwnerIds,
      dueDateTimestamp
    }).then(() => {
      dueDateUpdateSuccessAlert();
    }).catch(error => {
      const errorSubCategory = getErrorSubCategoryFromError(error);
      captureException(error, {
        errorSubCategory
      });
      genericErrorAlert();
    });
    onClose();
  }, [approval, stagedDueDate, stagedDueTime, onClose]);
  const hasChanges = !simpleDateEquals(stagedDueDate, initialDueDate) || stagedDueTime !== initialDueTime;
  const disableSaveButton = !isDueDateValid || !stagedDueDate && stagedDueTime !== undefined || !hasChanges;
  return /*#__PURE__*/_jsxs(Modal, {
    role: "dialog",
    "data-test-id": "approval-due-date-modal",
    width: 450,
    children: [/*#__PURE__*/_jsx(DialogHeader, {
      children: /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.ApprovalSidebar.dueDate.modalTitle"
        })
      })
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx(ApprovalDueDateInputGroup, {
        dueDate: stagedDueDate,
        setDueDate: setStagedDueDate,
        dueTime: stagedDueTime,
        setDueTime: setStagedDueTime,
        isDueDateValid: isDueDateValid,
        setIsDueDateValid: setIsDueDateValid
      })
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        placement: "top",
        title: !hasChanges && isDueDateValid ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.ApprovalSidebar.dueDate.noChangesTooltip"
        }) : undefined,
        disabled: hasChanges || !isDueDateValid,
        children: /*#__PURE__*/_jsx(Button, {
          "data-test-id": "approval-due-date-save-button",
          onClick: onSubmit,
          use: "primary",
          disabled: disableSaveButton,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "productApprovals.ApprovalSidebar.dueDate.save"
          })
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "secondary",
        onClick: onClose,
        "data-test-id": "approval-due-date-cancel-button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.cancel"
        })
      })]
    })]
  });
};